import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, RequestHandlerScreen } from 'common-src/components/base';
import {
  deletePatientIntegrationsAthena,
  generateAthenaUrl,
  getPatientFromAthena,
  getPatientIntegrationsAthena,
  getPatientIntegrationsAthenaRequest,
} from 'common-src/models/PatientIntegrationsAthena';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import {
  ActionsRow,
  BaseTable,
  CreatorItem,
  TablePagination,
  TextItem,
} from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { TabsViewNew } from 'src/components/tabs';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useAthenaDepartments from 'src/hooks/useAthenaDepartments';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useOpenWindow from 'src/hooks/useOpenWindow';
import useRequestLoading from 'src/hooks/useRequestLoading';

import styles from './Athena.module.scss';
import { AthenaTabs, DefaultTabInfo, TableHeaders } from './constants';
import { getTabInfo } from './helpers';

const Athena = ({ patientId }) => {
  const dispatch = useDispatch();

  const tabs = Object.keys(AthenaTabs);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [tabInfo, setTabInfo] = useState(DefaultTabInfo);
  const [filteredIntegrations, setFilteredIntegrations] = useState([]);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });

  const integrations = useCustomSelector((state) => getPatientIntegrationsAthena(state, patientId));
  const { departmentLabelByValue } = useAthenaDepartments(integrations?.[0]?.practiceId);

  const { openWindow } = useOpenWindow();
  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading([getPatientIntegrationsAthenaRequest({ patientId })]);

  useInterval(() => {
    dispatch(getPatientIntegrationsAthenaRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  useEffect(() => {
    const { tabInfo, activeIntegrations, inactiveIntegrations, allIntegrations } =
      getTabInfo(integrations);
    setTabInfo(tabInfo);

    switch (selectedTab) {
      case tabs[0]:
        setFilteredIntegrations(activeIntegrations);
        break;
      case tabs[1]:
        setFilteredIntegrations(inactiveIntegrations);
        break;
      default:
        setFilteredIntegrations(allIntegrations);
        break;
    }
  }, [selectedTab, integrations]);

  const getData = () =>
    filteredIntegrations.map((integration) => ({
      practiceId: (
        <TextItem
          isActive
          text={integration.practiceId}
          isUnderlined
          onClick={() =>
            dispatch(openModal(ModalType.INTEGRATION_ATHENA_DETAILS, { integration, patientId }))
          }
        />
      ),
      athenaPatientId: <TextItem isActive text={integration.athenaPatientId} />,
      department: (
        <TextItem isActive text={departmentLabelByValue[integration.department] ?? '-'} />
      ),
      createdAt: (
        <TextItem
          isActive
          text={moment(integration.createdAt).format('MM/DD/YYYY')}
          value={integration.createdAt}
        />
      ),
      createdBy: (
        <CreatorItem
          isActive
          creator={integration.createdBy}
          creatorName={integration.createdBy?.getName(false, true)}
          value={integration.createdBy?.getName(false, true)}
        />
      ),
      actions: (
        <ActionsRow
          options={[
            {
              id: 'edit-button',
              text: 'Edit',
              backgroundColor: '#555671B2',
              color: 'white',
              disabled: !!integration.deletedAt,
              onClick: () =>
                dispatch(openModal(ModalType.LINK_ATHENA_PATIENT, { patientId, integration })),
            },
            {
              id: 'open-button',
              text: 'Open',
              backgroundColor: '#375844CC',
              color: 'white',
              disabled: !!integration.deletedAt,
              onClick: () => {
                openWindow({
                  url: generateAthenaUrl(integration),
                  target: '_blank',
                });
              },
            },
            {
              id: 'fetch-patient-button',
              text: 'Show',
              backgroundColor: '#4A90E2CC',
              color: 'white',
              disabled: !!integration.deletedAt,
              onClick: () =>
                dispatch(
                  getPatientFromAthena(integration.athenaPatientId, integration.practiceId, {
                    successBlock: (data) => {
                      showAlert(AlertType.Success, '', 'Patient data fetched successfully!');
                      dispatch(openModal(ModalType.ATHENA_PATIENT_DETAILS, { data }));
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                ),
            },
            {
              id: 'mark-as-inactive-button',
              text: 'Inactive',
              backgroundColor: '#F01F51B2',
              color: 'white',
              disabled: !!integration.deletedAt,
              showWarning: true,
              onClick: () =>
                dispatch(
                  deletePatientIntegrationsAthena(integration.id, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'Integration deleted!');
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                ),
            },
          ]}
        />
      ),
    }));

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling integrations..." />;
  }

  return (
    <>
      <TabsViewNew
        classNames={['medical-tabs']}
        tabClassNames={['flex-none']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
        showZero
        rightContent={
          <Button
            id="link-athena-patient-button"
            text="Link Athena Patient"
            onClick={() => dispatch(openModal(ModalType.LINK_ATHENA_PATIENT, { patientId }))}
            backgroundColor={Colors.white}
            textColor={ColorsNew.darkGreen}
            iconSrc={CommonIcons.plusIcon}
            classNames={[styles.button]}
          />
        }
      />
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="athena-integrations"
        classNames={[
          'medical-table-new',
          filteredIntegrations?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No integrations"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'createdAt',
          label: 'Created',
          width: '15%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={filteredIntegrations} />
    </>
  );
};

Athena.propTypes = {
  patientId: PropTypes.number,
};

export default Athena;
